// Single source of truth for open roles. Read by the /careers/ overview, the
// /careers/{slug}/ role pages, meta-configs, schema-configs (JobPosting) and
// the sitemap. Add, edit or close a role here and everything follows.
//
// Rules for role copy: young, direct, founder-led. Short sentences, British
// spelling. No salary figures or commission rates. Do not name suppliers or
// distributors.

export interface CareerRole {
  /** URL slug: /careers/{slug}/ — also the anchor id on the overview. */
  slug: string
  index: string
  title: string
  tagline: string
  type: string
  employmentType: "FULL_TIME" | "INTERN" | "PART_TIME" | "CONTRACTOR"
  location: string
  image: string
  summary: string
  /** Optional inline link after the summary: "{lead} <a>{anchor}</a>." */
  summaryLink?: { lead: string; anchor: string; href: string }
  doList: string[]
  lookingFor: string[]
  why: string
  seo: {
    title: string
    description: string
    keywords: string
  }
  datePosted: string
  validThrough: string
}

export const careerRoles: CareerRole[] = [
  {
    slug: "ai-solution-business-development",
    index: "01",
    title: "AI Solution — Business Development",
    tagline: "Walk into Malaysian businesses and show them what AI can really do.",
    type: "Full-time",
    employmentType: "FULL_TIME",
    location: "Shah Alam, Selangor · Hybrid",
    image: "/images/careers/role-bd.jpg",
    summary:
      "You're the first person a business meets when it asks, \"Can AI actually help us?\" You find the companies with real problems, show them what agentic AI and our products can do, and turn \"hmm, interesting\" into \"when can we start?\" Then you stay close while our engineers make it work. Part explorer, part translator, part trusted friend.",
    doList: [
      "Find Malaysian businesses with real problems that AI agents can fix — and start the conversation",
      "Dig into how they actually work, map it to a clear AI solution, and make the payoff obvious in plain words",
      "Own the deal from first hello to signed partnership, side by side with our engineers",
      "Build the pipeline and playbooks that grow our AI business — and bring what you hear back to the team",
    ],
    lookingFor: [
      "You love people and you're properly excited about AI — you can make a sceptic curious",
      "Some B2B sales, consulting or business development experience, or a track record of getting people to say yes",
      "You pick up tech fast and explain it simply",
      "Self-driven, organised, and happy to own a number",
      "Bonus: a network across Malaysian businesses, or you've sold software before",
    ],
    why:
      "AI is the biggest shift Malaysian businesses will face this decade. You get to help them make the leap — with a product that really works, at a young company you'll help shape. You grow as fast as the deals you close.",
    seo: {
      title: "AI Business Development Job in Shah Alam, Malaysia",
      description:
        "Bring agentic AI to Malaysian businesses. Full-time, hybrid in Shah Alam, Selangor. Find real problems, shape the AI solution, own the deal end to end.",
      keywords:
        "AI business development job Malaysia, business development executive Shah Alam, AI sales job Selangor, B2B tech sales job Malaysia, agentic AI jobs Malaysia",
    },
    datePosted: "2026-06-15",
    validThrough: "2026-12-31T23:59:59+08:00",
  },
  {
    slug: "forward-deployed-engineer",
    index: "02",
    title: "Forward Deployed Engineer",
    tagline: "Take frontier AI out of the demo and into real Malaysian businesses.",
    type: "Full-time",
    employmentType: "FULL_TIME",
    location: "Shah Alam, Selangor · Hybrid",
    image: "/images/careers/role-fde.jpg",
    summary:
      "You're the bridge between what AI can do and what actually ships. You sit with our customers, build agentic AI on the ground, and own the result — not a ticket queue. One week you're wiring Claude into a client's WhatsApp. The next, you're rethinking how their whole front office runs.",
    doList: [
      "Get inside Malaysian businesses, learn how they really work, and design agents for their hardest workflows",
      "Build end to end — prototype to production — with Claude, TypeScript or Python, and our agent platform",
      "Plug into the messy real world: WhatsApp, accounting systems like SQL Accounting and AutoCount, CRMs and in-house tools",
      "Measure yourself by one thing: did the customer's business change? Then feed what you learn into better products",
    ],
    lookingFor: [
      "You ship. Live this week beats perfect next quarter",
      "Strong engineering basics in TypeScript or Python, and the curiosity to learn whatever the problem needs",
      "Good in front of customers — you listen, turn vague pain into a clear build, and skip the jargon",
      "You think in systems and outcomes, and ambiguity gets you going",
      "Bonus: you've shipped something with LLMs, agents or integrations that real people used",
    ],
    why:
      "You get your hands on the frontier, on problems that matter to real businesses, with the freedom to decide how. What you build goes live in weeks — and you get to see the difference with your own eyes.",
    seo: {
      title: "Forward Deployed Engineer Job in Malaysia (AI Agents)",
      description:
        "Build and deploy AI agents with Claude inside real Malaysian businesses. Full-time, hybrid in Shah Alam, Selangor. TypeScript or Python, customer-facing.",
      keywords:
        "forward deployed engineer Malaysia, AI engineer job Malaysia, AI agent developer job Selangor, LLM engineer Malaysia, Claude developer job, software engineer Shah Alam",
    },
    datePosted: "2026-06-15",
    validThrough: "2026-12-31T23:59:59+08:00",
  },
  {
    slug: "gtm-engineer",
    index: "03",
    title: "GTM Engineer — Tech Sourcing",
    tagline:
      "Not a sales engineer. An engineer who builds the AI machine that sources technology for our clients — and runs it.",
    type: "Full-time",
    employmentType: "FULL_TIME",
    location: "Shah Alam, Selangor · Hybrid",
    image: "/images/careers/role-sourcing.jpg",
    summary:
      "Clients come to us for software, AI tools and hardware from the world's leading brands. Requests land every working day, often on tight deadlines. You own the whole flow: turn a request into a sharp quote fast, then get it ordered, provisioned and renewed on time. Do it by hand first. Then automate it with AI, end to end, until nobody beats us on speed.",
    doList: [
      "Own every sourcing request: work out what the client needs, get pricing, quote, order and follow through to payment",
      "Run it by hand, log every pain point, then automate it end to end with Claude, agents and workflows — from request to quote to a live, provisioned service",
      "Keep our CRM spotless so every deal and its status is clear",
      "Spot what clients will need next — licences, renewals, hardware — before they ask",
    ],
    lookingFor: [
      "You've built and shipped real automations: scripts, agents, n8n/Make/Power Automate workflows. Show us",
      "Sharp with spreadsheets and numbers",
      "Happy talking to clients and suppliers, and not shy about chasing",
      "Obsessive about accuracy — every quote carries our name",
      "Bonus: software licensing know-how, CRM experience, Bahasa Malaysia or Mandarin",
    ],
    why:
      "You're builder and closer in one. You sit where AI meets real business buying, and every week brings new products and live deals. Speed wins here, and you build the machine that makes us fastest. Basic salary plus commission on every deal you convert — so your upside grows with the business.",
    seo: {
      title: "GTM Engineer Job in Malaysia: Tech Sourcing & Automation",
      description:
        "An engineer, not a pure salesperson. Own tech sourcing end to end and automate it with Claude and agents. Full-time, hybrid in Shah Alam. Basic plus commission.",
      keywords:
        "GTM engineer Malaysia, go-to-market engineer job, sales automation engineer Malaysia, AI automation job Selangor, n8n automation job Malaysia, tech sourcing job Shah Alam",
    },
    datePosted: "2026-11-03",
    validThrough: "2027-05-31T23:59:59+08:00",
  },
  {
    slug: "product-engineer-intern",
    index: "04",
    title: "Product Engineer Intern",
    tagline: "Ship real AI product from day one. No coffee runs.",
    type: "Internship",
    employmentType: "INTERN",
    location: "Shah Alam, Selangor · Hybrid",
    image: "/images/careers/role-intern.jpg",
    summary:
      "This isn't a watch-and-learn internship. You'll write code for client AI agent projects and build features on our in-house products (EzyChat, which is public, plus EzyTeam, EzySQL and MySalesFlow, still in private testing). You'll learn how modern AI software really gets made — fast — next to engineers who'll push you to level up.",
    summaryLink: {
      lead: "Try EzyChat, our",
      anchor: "free open-source WhatsApp team inbox",
      href: "https://ezychat.ai/",
    },
    doList: [
      "Build real features across our web app and AI products, front to back",
      "Work with Claude and agentic AI — prompt it, test it, plug it in — and learn what makes an AI product good",
      "Prototype on Monday. Demo on Friday",
      "Own a real slice of product, with mentoring and code reviews that level you up fast",
    ],
    lookingFor: [
      "You're a student or fresh grad who builds things — side projects, hackathons, anything you couldn't stop tinkering with",
      "Some coding in any language (we use TypeScript/React and Python). Hunger beats a perfect CV",
      "You learn fast, ask sharp questions, and don't mind being wrong",
      "Bonus: you've played with AI or LLMs, shipped a side project, or have a good eye for design",
    ],
    why:
      "Most internships hand you a sandbox. We hand you the real thing. You'll leave having shipped production AI features on frontier models, and learnt more in a few months than a year of theory. Our best interns go full-time.",
    seo: {
      title: "Software Engineer Internship in Selangor: AI Product",
      description:
        "Ship real AI features with Claude, TypeScript and Python from day one. A hybrid internship in Shah Alam, Selangor, for students and fresh grads.",
      keywords:
        "software engineer intern Selangor, software engineering internship Malaysia, AI internship Shah Alam, tech internship Malaysia, product engineer intern, React internship Malaysia",
    },
    datePosted: "2026-06-15",
    validThrough: "2026-12-31T23:59:59+08:00",
  },
]

export const careerRolePath = (role: CareerRole) => `/careers/${role.slug}/`

export const getCareerRole = (slug: string) => careerRoles.find((role) => role.slug === slug)

/** Full HTML job description for JobPosting JSON-LD (Google renders basic HTML). */
export function getCareerRoleJobDescriptionHtml(role: CareerRole): string {
  const list = (items: string[]) => `<ul>${items.map((item) => `<li>${item}</li>`).join("")}</ul>`
  return [
    `<p><strong>${role.tagline}</strong></p>`,
    `<p>${role.summary}</p>`,
    `<p><strong>What you'll do</strong></p>`,
    list(role.doList),
    `<p><strong>What we're looking for</strong></p>`,
    list(role.lookingFor),
    `<p><strong>Why it's exciting</strong></p>`,
    `<p>${role.why}</p>`,
    `<p>${role.location}. Apply via https://www.anchorsprint.com/contact/.</p>`,
  ].join("")
}
